import type {RGBA} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';
import {FillTypes, getFillColors} from '@PosterWhiteboard/classes/fill.types';
import {createFillForSurface, FillSurface, getBackgroundColorsForFillType} from '@PosterWhiteboard/classes/fill-surface';
import {DEFAULT_BACKGROUND_COLOR, DEFAULT_SECONDARY_BACKGROUND_COLOR, FULL_PAGE_OFFSET, FULL_PAGE_PADDING} from '@PosterWhiteboard/page/page.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {DeepPartial} from '@/global';
import {getUniqueString} from '@Utils/string.util';

/**
 * Fill type to build when a colour is applied to a background. A transparent (NONE) background
 * has no colour to edit, so applying a colour turns it solid; every other type keeps its own.
 */
export const getFillTypeForAppliedColor = (currentFillType: FillTypes): FillTypes => {
  return currentFillType === FillTypes.NONE ? FillTypes.SOLID : currentFillType;
};

/**
 * Default colour set for a target fill type. When a colour background is already active its existing
 * colours are adapted to the new type; otherwise sensible defaults are used — one colour for
 * solid/transparent, two for gradients.
 */
export const getDefaultColorsForFillType = (fillType: FillTypes): RGBA[] => {
  const activeBg = window.posterEditor?.whiteboard?.getCurrentPage()?.getActiveBackground();
  if (activeBg?.isRectangle()) {
    return getBackgroundColorsForFillType(fillType, getFillColors(activeBg.getFillObject()));
  }

  const primaryDefaultColor = hexToRgba(DEFAULT_BACKGROUND_COLOR);
  switch (fillType) {
    case FillTypes.SOLID:
    case FillTypes.NONE:
      return [primaryDefaultColor];
    case FillTypes.LINEAR_GRADIENT:
    case FillTypes.RADIAL_GRADIENT:
      return [primaryDefaultColor, hexToRgba(DEFAULT_SECONDARY_BACKGROUND_COLOR)];
    default:
      return assertNever(fillType);
  }
};

/** Resizes a color background item object in place to fill a poster of the given new dimensions; the rectangle redraws its fill over the new box. */
export const resizeColorBackgroundItemObject = (bgObject: RectangleItemObject, newPosterWidth: number, newPosterHeight: number): void => {
  bgObject.left = FULL_PAGE_OFFSET;
  bgObject.top = FULL_PAGE_OFFSET;
  bgObject.width = newPosterWidth + FULL_PAGE_PADDING;
  bgObject.height = newPosterHeight + FULL_PAGE_PADDING;
  bgObject.scaleX = 1;
  bgObject.scaleY = 1;
};

/** Constructs a partial RectangleItemObject for a color/gradient/transparent bg covering the page. */
export const buildColorBackgroundItemObject = (
  page: Page,
  fillType: FillTypes,
  fillColors: RGBA[],
  opts: {uid?: string; onPageStartTime?: number; onPageEndTime?: number} = {}
): DeepPartial<RectangleItemObject> => {
  return {
    gitype: ITEM_TYPE.RECTANGLE,
    isBackground: true,
    uid: opts.uid ?? getUniqueString(),
    onPageStartTime: opts.onPageStartTime ?? 0,
    // When adding the first background to a page that has items but no bgs, page.getDuration()
    // returns the max item end time — sizing the bg here keeps items from being truncated. On a
    // page with no time-based content that duration is 0, and the background stays spanless with
    // everything else there: a colour swap must not be what gives a poster a timeline.
    onPageEndTime: opts.onPageEndTime ?? page.getDuration(),
    left: FULL_PAGE_OFFSET,
    top: FULL_PAGE_OFFSET,
    width: page.poster.width + FULL_PAGE_PADDING,
    height: page.poster.height + FULL_PAGE_PADDING,
    scaleX: 1,
    scaleY: 1,
    rx: 0,
    ry: 0,
    fill: createFillForSurface(fillType, fillColors, FillSurface.BACKGROUND),
  };
};
